/* DVY offer selector, sticky add-to-cart and product gallery. Vanilla web components. */

/**
 * Normalizes a selling plan label like "Delivery every 30 Days" to "30 days".
 * @param {string} label
 */
function dvyFrequencyText(label) {
  const match = /(\d+)\s*(day|week|month)s?/i.exec(label || '');
  if (!match) return label || '';
  const n = Number(match[1]);
  const unit = match[2].toLowerCase();
  return `${n} ${unit}${n === 1 ? '' : 's'}`;
}

/** Converts a plan label to an approximate number of days, for matching default frequencies. */
function dvyFrequencyDays(label) {
  const match = /(\d+)\s*(day|week|month)s?/i.exec(label || '');
  if (!match) return 0;
  const n = Number(match[1]);
  const unit = match[2].toLowerCase();
  return unit === 'week' ? n * 7 : unit === 'month' ? n * 30 : n;
}

/** Replaces [token] placeholders in merchant copy. */
function dvyFill(template, values) {
  return String(template || '').replace(/\[(\w+)\]/g, (all, key) => (key in values ? values[key] : all));
}

/** Formats cents as dollars, dropping ".00" (US store). */
function dvyMoney(cents) {
  const dollars = cents / 100;
  return `$${Number.isInteger(dollars) ? dollars : dollars.toFixed(2)}`;
}

/** Publishes a Shopify customer event (forwarded to GA4 by a Customer Events pixel). */
function dvyPublish(name, data) {
  try {
    window.Shopify?.analytics?.publish?.(name, data);
  } catch (error) {
    // Analytics must never block the offer.
  }
}

class DvyOfferSelector extends HTMLElement {
  connectedCallback() {
    const dataEl = this.querySelector('[data-dvy-offer-data]');
    try {
      this.data = JSON.parse(dataEl.textContent);
    } catch (error) {
      console.error('DVY offer: invalid data', error);
      return;
    }
    this.form = this.querySelector('[data-dvy-offer-form]');
    this.planInput = this.querySelector('[data-dvy-selling-plan]');
    this.subTile = this.querySelector('[data-dvy-sub-tile]');
    this.freqWrap = this.querySelector('[data-dvy-freq]');
    this.submitButton = this.querySelector('[data-dvy-submit]');
    this.errorEl = this.querySelector('[data-dvy-error]');
    this.liveEl = this.querySelector('[data-dvy-live]');
    this.selectedPlanByVariant = {};

    this.applyUrlParams();

    this.form.addEventListener('change', (event) => {
      const target = event.target;
      if (target.matches('[data-dvy-freq-input]')) {
        this.selectedPlanByVariant[this.variant.id] = Number(target.value);
      }
      if (target.name === 'id') this.clearError();
      this.update();
      dvyPublish('dvy_offer_select', this.trackingData());
    });
    this.form.addEventListener('submit', (event) => this.onSubmit(event));
    this.update();
  }

  get variantInputs() {
    return [...this.form.querySelectorAll('input[name="id"]')];
  }

  /** Highest subscription saving across all sizes, for "save up to [max_percent]%". */
  get maxPercent() {
    return Math.max(0, ...this.data.variants.flatMap((v) => (v.plans || []).map((p) => p.percent || 0)));
  }

  get variant() {
    const checked = this.variantInputs.find((input) => input.checked);
    const id = Number(checked?.value);
    return this.data.variants.find((v) => v.id === id) || this.data.variants[0];
  }

  get purchaseInputs() {
    return [...this.form.querySelectorAll('[data-dvy-purchase]')];
  }

  get isSubscribe() {
    return this.purchaseInputs.find((input) => input.checked)?.value === 'subscribe';
  }

  /** Subscription plan a variant defaults to (its default frequency, else its first plan). */
  defaultPlanFor(variant) {
    if (!variant?.plans?.length) return null;
    const preferred = variant.defaultFrequency && variant.plans.find((p) => dvyFrequencyDays(p.label) === variant.defaultFrequency);
    return preferred || variant.plans[0];
  }

  /** Whole-dollar subscription saving vs. one-time price + one-time shipping. */
  savingsFor(variant, plan) {
    if (!plan) return 0;
    return Math.max(0, Math.round((variant.priceCents + (this.data.shippingCents || 0) - plan.priceCents) / 100));
  }

  /** Subscribe on a size that earns the bonus guide. */
  get qualifiesForBonus() {
    return Boolean(this.data.bonusEnabled && this.isSubscribe && this.currentPlan() && this.variant.boxes >= this.data.bonusMinBoxes);
  }

  trackingData() {
    const plan = this.currentPlan();
    const subscribe = this.isSubscribe && Boolean(plan);
    return {
      bags: this.variant.boxes,
      plan: subscribe ? 'sub' : 'once',
      price: (subscribe ? plan.priceCents : this.variant.priceCents) / 100,
      bonus: this.qualifiesForBonus,
    };
  }

  /** ?bags=1|2|6 (alias ?boxes=), ?plan=onetime|once|subscribe|sub, ?variant=<id>, ?selling_plan=<id> preselect options (used by ads). */
  applyUrlParams() {
    const params = new URLSearchParams(window.location.search);
    const boxes = params.get('bags') || params.get('boxes');
    const variantId = params.get('variant');
    const plan = params.get('plan');

    let target = null;
    if (boxes) target = this.variantInputs.find((input) => input.dataset.boxes === boxes);
    if (!target && variantId) target = this.variantInputs.find((input) => input.value === variantId);
    if (target && !target.disabled) target.checked = true;

    // Make sure a purchasable variant is selected.
    const current = this.variantInputs.find((input) => input.checked);
    if (!current || current.disabled) {
      const firstAvailable = this.variantInputs.find((input) => !input.disabled);
      if (firstAvailable) firstAvailable.checked = true;
    }

    if (['onetime', 'one-time', 'once'].includes(plan)) this.setPurchase('onetime');
    else if (['subscribe', 'subscription', 'sub'].includes(plan)) this.setPurchase('subscribe');

    // ?selling_plan=<id> (advertorial handoff): subscribe on that plan if the selected size offers it.
    const sellingPlan = Number(params.get('selling_plan'));
    if (sellingPlan && this.variant.plans.some((p) => p.id === sellingPlan)) {
      this.selectedPlanByVariant[this.variant.id] = sellingPlan;
      this.setPurchase('subscribe');
    }
  }

  setPurchase(value) {
    const input = this.purchaseInputs.find((el) => el.value === value);
    if (input) input.checked = true;
  }

  currentPlan() {
    const variant = this.variant;
    if (!variant.plans.length) return null;
    const chosen = this.selectedPlanByVariant[variant.id];
    return variant.plans.find((p) => p.id === chosen) || this.defaultPlanFor(variant);
  }

  renderChips(variant, plan) {
    const name = `dvy_freq_${this.dataset.sectionId}`;
    this.freqWrap.replaceChildren(
      ...variant.plans.map((p) => {
        const label = document.createElement('label');
        label.className = 'dvy-chip';
        const input = document.createElement('input');
        input.type = 'radio';
        input.name = name;
        input.value = String(p.id);
        input.checked = plan && p.id === plan.id;
        input.dataset.dvyFreqInput = '';
        const text = document.createElement('span');
        text.textContent = dvyFrequencyText(p.label);
        label.append(input, text);
        return label;
      })
    );
    this.freqWrap.parentElement.hidden = variant.plans.length < 2;
  }

  update() {
    const variant = this.variant;
    const hasPlans = variant.plans.length > 0;

    // Hide the subscribe tile gracefully when this variant has no selling plans.
    this.subTile.hidden = !hasPlans;
    if (!hasPlans && this.isSubscribe) this.setPurchase('onetime');
    if (hasPlans && !this.purchaseInputs.some((input) => input.checked)) {
      this.setPurchase(this.dataset.defaultPlan === 'onetime' ? 'onetime' : 'subscribe');
    }

    const plan = this.currentPlan();
    const subscribe = this.isSubscribe && Boolean(plan);
    const frequency = plan ? dvyFrequencyText(plan.label) : '';
    const shipping = dvyMoney(this.data.shippingCents || 0).slice(1);
    const savings = this.savingsFor(variant, plan);
    const buttonNote = dvyFill(subscribe ? this.data.buttonNoteSub : this.data.buttonNoteOnetime, { shipping });
    const tokens = {
      savings,
      shipping,
      ship_note: buttonNote,
      bags: variant.bags || '',
      boxes: variant.boxes || '',
      label: variant.label || '',
      frequency,
      percent: plan ? plan.percent : '',
      max_percent: this.maxPercent,
      price: subscribe ? plan.price : variant.price,
      per_day: (subscribe ? plan.perDay : variant.perDay) || '',
    };

    // Subscribe tile
    if (plan) {
      this.renderChips(variant, plan);
      const noSaving = /\s*[&+]?\s*save\s*(up to\s*)?(\$?\[savings\]|\[(max_)?percent\]%?)/i;
      // "[savings]" is this size's dollar saving, "[percent]" its % saving, "[max_percent]" the best across sizes; drop the phrase when it's 0.
      const titleSaving = /\[savings\]/.test(this.data.subTitle) ? savings : /\[max_percent\]/.test(this.data.subTitle) ? this.maxPercent : plan.percent;
      const subTitle = titleSaving > 0 ? this.data.subTitle : this.data.subTitle.replace(noSaving, '');
      // Per-size saving sentence (e.g. "Save 16% on this size · ") drops out when this size saves nothing.
      const subDesc = plan.percent > 0 ? this.data.subDesc : this.data.subDesc.replace(/[^·]*\[percent\][^·]*·\s*/i, '');
      this.setText('[data-dvy-sub-title]', dvyFill(subTitle, tokens));
      this.setText('[data-dvy-sub-desc]', dvyFill(subDesc, tokens));
      this.setText('[data-dvy-sub-price]', plan.price);
      this.setText('[data-dvy-sub-compare]', plan.compare || '');
      this.setText('[data-dvy-sub-perday]', plan.perDay ? plan.perDay + this.data.perDaySuffix : '');
      // With a single delivery frequency there are no chips, so state the interval as text.
      const interval = variant.plans.length === 1 && this.data.subInterval ? dvyFill(this.data.subInterval, tokens) : '';
      this.setText('[data-dvy-sub-interval]', interval);
      this.querySelector('[data-dvy-sub-interval]').hidden = !interval;
    }

    // Bonus guide: shown on qualifying subscriptions, nudge on smaller subscriptions, nothing for one-time.
    const bonusEl = this.querySelector('[data-dvy-bonus]');
    const nudgeEl = this.querySelector('[data-dvy-bonus-nudge]');
    if (bonusEl) bonusEl.hidden = !this.qualifiesForBonus;
    if (nudgeEl) nudgeEl.hidden = !(subscribe && !this.qualifiesForBonus);

    // Per-day subscription price (and saving, where enabled) on every bag card.
    this.variantInputs.forEach((input) => {
      const card = input.closest('label');
      const cardVariant = this.data.variants.find((v) => v.id === Number(input.value));
      const cardPlan = this.defaultPlanFor(cardVariant);
      const perDayEl = card.querySelector('[data-dvy-card-perday]');
      const saveEl = card.querySelector('[data-dvy-card-save]');
      const perDay = cardPlan?.perDay || cardVariant?.perDay;
      if (perDayEl) perDayEl.textContent = perDay ? dvyFill(this.data.cardPerDay, { sub_per_day: perDay, per_day: perDay }) : '';
      const cardSavings = this.savingsFor(cardVariant, cardPlan);
      if (saveEl) saveEl.textContent = cardSavings > 0 ? dvyFill(this.data.cardSavings, { savings: cardSavings }) : '';
    });

    // One-time tile
    this.setText('[data-dvy-onetime-desc]', dvyFill(this.data.onetimeDesc, tokens));
    this.setText('[data-dvy-onetime-price]', variant.price);
    this.setText('[data-dvy-onetime-perday]', variant.perDay ? variant.perDay + this.data.perDaySuffix : '');

    // Selling plan is only submitted for subscriptions.
    this.planInput.disabled = !subscribe;
    this.planInput.value = subscribe ? String(plan.id) : '';

    // Button
    const available = variant.available;
    this.submitButton.disabled = !available;
    this.setText('[data-dvy-submit-label]', available ? dvyFill(subscribe ? this.data.subLabel : this.data.onetimeLabel, tokens) : this.data.soldOutLabel);
    this.setText('[data-dvy-submit-price]', available ? tokens.price : '');
    this.setText('[data-dvy-submit-note]', available ? buttonNote : '');

    const detail = {
      sectionId: this.dataset.sectionId,
      available,
      subscribe,
      price: tokens.price,
      summary: dvyFill(subscribe ? this.data.stickySub : this.data.stickyOnetime, tokens),
      note: dvyFill(this.data.stickyNote, tokens),
      button: available ? dvyFill(this.data.stickyButton, tokens) : this.data.soldOutLabel,
    };
    this.lastDetail = detail;
    this.dispatchEvent(new CustomEvent('dvy:offer:change', { bubbles: true, detail }));
  }

  setText(selector, text) {
    const el = this.querySelector(selector);
    if (el) el.textContent = text;
  }

  clearError() {
    this.errorEl.hidden = true;
    this.errorEl.textContent = '';
  }

  async onSubmit(event) {
    event.preventDefault();
    if (this.submitButton.disabled) return;
    this.clearError();
    this.submitButton.setAttribute('aria-busy', 'true');
    this.submitButton.disabled = true;

    const body = new FormData(this.form);
    // Only Shopify's fields are sent: id, quantity, selling_plan (subscriptions only).
    const payload = { items: [{ id: Number(body.get('id')), quantity: 1 }] };
    if (!this.planInput.disabled && this.planInput.value) payload.items[0].selling_plan = Number(this.planInput.value);
    if (this.qualifiesForBonus) payload.items[0].properties = { _bonus_guide: 'yes' };
    const tracking = this.trackingData();

    try {
      // ?appstle=true makes the Appstle storefront script skip this request; otherwise its
      // fetch wrapper overwrites selling_plan with its own (absent) widget value.
      const response = await fetch(`${window.Shopify?.routes?.root || '/'}cart/add.js?appstle=true`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(payload),
      });
      const result = await response.json();
      if (!response.ok) throw new Error(result.description || result.message || this.data.errorMessage);
      this.liveEl.textContent = this.data.addedMessage || '';
      dvyPublish('dvy_offer_add_to_cart', tracking);
      document.dispatchEvent(new CustomEvent('dvy:cart:added', { detail: { items: result.items || [result], source: this } }));
      if (!customElements.get('dvy-cart-drawer') || !document.querySelector('dvy-cart-drawer')) {
        window.location.href = this.dataset.cartUrl;
      }
    } catch (error) {
      this.errorEl.textContent = error.message || this.data.errorMessage;
      this.errorEl.hidden = false;
    } finally {
      this.submitButton.removeAttribute('aria-busy');
      this.submitButton.disabled = !this.variant.available;
    }
  }
}

/** Mobile sticky add-to-cart bar; mirrors the offer selector in the same section. */
class DvyStickyAtc extends HTMLElement {
  connectedCallback() {
    const sectionId = this.dataset.sectionId;
    this.selector = document.querySelector(`dvy-offer-selector[data-section-id="${sectionId}"]`);
    if (!this.selector) return;
    this.button = this.querySelector('[data-dvy-sticky-button]');

    const render = (detail) => {
      if (!detail) return;
      this.querySelector('[data-dvy-sticky-summary]').textContent = detail.summary;
      this.querySelector('[data-dvy-sticky-note]').textContent = detail.note;
      this.button.textContent = detail.button;
      this.button.disabled = !detail.available;
    };
    render(this.selector.lastDetail);
    this.selector.addEventListener('dvy:offer:change', (event) => render(event.detail));

    this.button.addEventListener('click', () => {
      dvyPublish('sticky_atc_click', this.selector.trackingData?.() || {});
      const submit = this.selector.querySelector('[data-dvy-submit]');
      this.selector.querySelector('form').requestSubmit(submit);
    });

    // Visible from load; hidden only while the main button or the footer is on screen. Phones only (CSS hides it from 990px).
    const target = this.selector.querySelector('[data-dvy-submit]');
    const footer = document.querySelector('footer, .dvy-footer');
    let frame = 0;
    const check = () => {
      frame = 0;
      const footerIn = footer ? footer.getBoundingClientRect().top < window.innerHeight : false;
      const rect = target.getBoundingClientRect();
      const ctaInView = rect.bottom > 0 && rect.top < window.innerHeight;
      const passed = !ctaInView && !footerIn;
      if (passed === this.hasAttribute('data-visible')) return;
      this.toggleAttribute('data-visible', passed);
      this.setAttribute('aria-hidden', String(!passed));
      this.button.tabIndex = passed ? 0 : -1;
    };
    const schedule = () => {
      if (!frame) frame = requestAnimationFrame(check);
    };
    window.addEventListener('scroll', schedule, { passive: true, capture: true });
    window.addEventListener('resize', schedule, { passive: true });
    check();
  }
}

/** Product gallery: scroll-snap track with thumbnail buttons. */
class DvyGallery extends HTMLElement {
  connectedCallback() {
    this.track = this.querySelector('[data-dvy-gallery-track]');
    this.thumbs = [...this.querySelectorAll('[data-dvy-gallery-thumb]')];
    this.thumbs.forEach((thumb, index) => {
      thumb.addEventListener('click', () => this.go(index));
    });
    if (!this.track || !this.thumbs.length) return;
    this.track.addEventListener(
      'scroll',
      () => {
        const index = Math.round(this.track.scrollLeft / this.track.clientWidth);
        this.mark(index);
      },
      { passive: true }
    );
  }

  go(index) {
    const slide = this.track.children[index];
    if (!slide) return;
    this.track.scrollTo({ left: slide.offsetLeft - this.track.offsetLeft, behavior: 'smooth' });
    this.mark(index);
  }

  mark(index) {
    this.thumbs.forEach((thumb, i) => thumb.setAttribute('aria-current', String(i === index)));
    const count = this.querySelector('[data-dvy-gallery-index]');
    if (count) count.textContent = index + 1;
  }
}

if (!customElements.get('dvy-offer-selector')) customElements.define('dvy-offer-selector', DvyOfferSelector);
if (!customElements.get('dvy-sticky-atc')) customElements.define('dvy-sticky-atc', DvyStickyAtc);
if (!customElements.get('dvy-gallery')) customElements.define('dvy-gallery', DvyGallery);

/**
 * PDP measurement + in-page CTAs. Events go through Shopify.analytics.publish (Customer Events pixel → GA4):
 * section_view {section_id} once per section at 50% visible (or half the viewport for tall sections),
 * quick_answer_open {question}. Section CTAs linking to #offer smooth-scroll to the buy box.
 */
(() => {
  if (!document.querySelector('.dvy-main-product-section, .dvy-offer-section')) return;
  const reduced = matchMedia('(prefers-reduced-motion: reduce)').matches;
  document.addEventListener('click', (event) => {
    const link = event.target.closest('a[href="#offer"]');
    const offer = document.getElementById('offer');
    if (!link || !offer) return;
    event.preventDefault();
    offer.scrollIntoView({ behavior: reduced ? 'auto' : 'smooth', block: 'start' });
  });
  document.querySelectorAll('[data-dvy-quick-answer]').forEach((details) => {
    details.addEventListener('toggle', () => {
      if (details.open) dvyPublish('quick_answer_open', { question: details.querySelector('summary').textContent.trim() });
    });
  });
  if (!('IntersectionObserver' in window)) return;
  const io = new IntersectionObserver((entries) => {
    entries.forEach((entry) => {
      const half = entry.intersectionRatio >= 0.5 || entry.intersectionRect.height >= window.innerHeight * 0.5;
      if (!entry.isIntersecting || !half) return;
      io.unobserve(entry.target);
      dvyPublish('section_view', { section_id: entry.target.id.replace(/^shopify-section-(template--\d+__)?/, '') });
    });
  }, { threshold: [0, 0.25, 0.5, 0.75, 1] });
  // Start after window load so the Customer Events pixel is subscribed before the first (main) view fires.
  const start = () => setTimeout(() => {
    document.querySelectorAll('main .shopify-section').forEach((section) => {
      if (section.offsetHeight > 0) io.observe(section);
    });
  }, 500);
  if (document.readyState === 'complete') start();
  else window.addEventListener('load', start, { once: true });
})();
